/**
 * HV vs IV — Historical Volatility vs ATM Implied Volatility
 * When IV > HV: options overpriced — favour premium selling
 * When IV < HV: options cheap — favour premium buying
 */
import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Box, Typography, Chip } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';

interface HistoryRow {
  computedAt: string;
  atmIv?: number;
  hv30d?: number;
  ivRank52w?: number;
}

interface Props {
  history: HistoryRow[];
  tickFormatter?: (ts: string) => string;
}

const HVvsIVChart: React.FC<Props> = ({ history, tickFormatter }) => {
  const { C } = useAppTheme();

  if (!history || history.length === 0) return null;

  const latest = history[history.length - 1];
  const isPremiumRich = (latest?.atmIv || 0) > (latest?.hv30d || 0);

  const fmt = tickFormatter || ((ts: string) => new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }));
  const fmtFull = (ts: string) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : '';

  const data = history.map(r => ({
    ts: r.computedAt,
    'ATM IV': r.atmIv ? Number(r.atmIv).toFixed(2) : null,
    'HV 30d': r.hv30d ? Number(r.hv30d).toFixed(2) : null
  }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>HV vs IV — Volatility Premium</Typography>
        <Chip
          size="small"
          label={isPremiumRich ? 'IV > HV — Sell Premium' : 'IV < HV — Buy Premium'}
          sx={{
            bgcolor: isPremiumRich ? C.bearMuted : C.bullMuted,
            color:   isPremiumRich ? C.bear : C.bull,
            fontSize: '0.7rem', fontWeight: 700
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
        {isPremiumRich
          ? 'IV exceeds historical vol — options are expensive relative to actual movement.'
          : 'IV below historical vol — options are cheap relative to actual movement.'}
      </Typography>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id="ivGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={C.chartLine1} stopOpacity={0.3} />
              <stop offset="95%" stopColor={C.chartLine1} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="hvGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={C.chartBull} stopOpacity={0.3} />
              <stop offset="95%" stopColor={C.chartBull} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} opacity={0.4} />
          <XAxis dataKey="ts" tickFormatter={fmt} tick={{ fontSize: 10 }} minTickGap={60} />
          <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
          <Tooltip
            labelFormatter={fmtFull}
            formatter={(v: any) => [`${v}%`]}
            contentStyle={{ backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }}
          />
          <Legend />
          <Area type="monotone" dataKey="ATM IV" stroke={C.chartLine1} fill="url(#ivGrad)" strokeWidth={2} dot={false} connectNulls />
          <Area type="monotone" dataKey="HV 30d" stroke={C.chartBull}  fill="url(#hvGrad)" strokeWidth={2} dot={false} connectNulls />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default HVvsIVChart;
